import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { withLastLocation } from 'react-router-last-location';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import _ from 'lodash';
import cn from 'classnames';
import { formatAmountInCurrency } from '../../Helpers/formatting';
import './ProductMainSection.scss';

import ProductHeart from '../Shop/Elements/ProductHeart';

import { deleteUserProductByProductId, createUserProductByProductId } from '../../Actions/ShopActions';

import {
  getLinkToBrandPageViaId,
  getLinkToCategoryPageViaId,
  getAttributableCuratorId,
  getAttributableCuratorGroupId,
  getLinkPriceDisplay,
  isLinkOutOfStock,
  isLinkDiscounted,
  getLinkStandardPriceDisplay
} from '../../Helpers/shop_helpers';
import { addVariablesToShopAffiliateLink, registerPinClickEventForProduct } from '../../Helpers/attribution_helpers';
import { getDomainFromUrl } from '../../Helpers/formatting';
import { tooltipKeys, shouldShowTooltip } from '../../Helpers/onboarding_helpers';

import CuratorCountDisplay from '../Curators/Elements/CuratorCountDisplay';
import TrainingTooltip from '../General/TrainingTooltip';

const ProductMainSection = props => {
  const { shop, user, product, isFetchingFull, lastLocation, history } = props;
  const { category, brand, description } = product || {};
  const links = product?.links || [];
  const primaryLink = links[0];
  const other_images = _.orderBy(product?.images || [], ['isCover', 'sortOrderRank'], ['desc', 'asc']);

  // Visibility on all links modal

  // Use ID it in React so we can update the state
  const [featuredProductImageId, setFeaturedProductImageId] = React.useState(null);
  const featuredProductImage = other_images.find(p => p.id === featuredProductImageId) || other_images[0];
  const setFeaturedProductImage = productImage => setFeaturedProductImageId(productImage.id);
  React.useEffect(() => {
    if (!isFetchingFull && !featuredProductImage) setFeaturedProductImageId(other_images[0]);
  }, [isFetchingFull]);

  // Description (brand names can have periods so need to cast it to a variable)
  const sentences = description?.replaceAll(brand?.name, 'BRANDNAME')?.split('. ') || [];
  const firstSentence = (sentences[0] + '. ').replaceAll('BRANDNAME', brand?.name);
  const restOfDescription = description?.replace(firstSentence, '').replaceAll('BRANDNAME', brand?.name);

  // Back Button
  const canGoBack = !!lastLocation && !lastLocation.pathname.includes('/shop/product/');
  const clickGoBack = () => canGoBack && history.goBack();

  // Handle Image Issues
  const [brokenImages, setBrokenImages] = React.useState([]);
  const imageBroke = image => setBrokenImages([...brokenImages, image]);
  const visibleOtherImages = other_images.filter(p => !brokenImages.includes(p.image));

  // Beautiful Image Loading
  const [primaryImageLoaded, setPrimaryImageLoaded] = React.useState(false);
  const onPrimaryImageLoad = () => {
    setTimeout(() => {
      setPrimaryImageLoaded(true);
    }, 250); // Smooth Loading (time with CSS classes below)
  };

  // Reset animation on product ID change
  const prevProductId = React.useRef(product?.id); // To handle hot reload issues
  React.useEffect(() => {
    if (product?.id !== prevProductId.current) setPrimaryImageLoaded(false);
    prevProductId.current = product?.id;
  }, [product?.id]);

  // Training Tooltip
  const showAddProductTrainingTooltip = shouldShowTooltip(tooltipKeys.ADD_TO_WISHLIST_FROM_PRODUCT_PAGE, { user, shop });

  const additionalClasses = cn({ fetching: isFetchingFull }, featuredProductImage?.style || '');
  return (
    <div className='product-main-section-container'>
      <div className='product-data-outer-container'>
        <div className={cn('product-data-inner-container', additionalClasses)}>
          <div className={cn('main', additionalClasses)}>
            {isFetchingFull ? (
              <>
                <div className='metadata loading' />
                <div className='title loading' />
                <div className='curator-count loading' />
              </>
            ) : (
              <>
                {canGoBack && (
                  <div onClick={clickGoBack} className='go-back-btn fade-in fifth'>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Back
                  </div>
                )}
                <div className='metadata'>
                  <Link to={getLinkToBrandPageViaId(brand?.id)} className='clickable fade-in first'>
                    {brand?.name || product?.brand?.name}
                  </Link>
                  {category && <span className='separator fade-in first'>•</span>}
                  {category && (
                    <Link to={getLinkToCategoryPageViaId(category.id)} className='clickable fade-in first'>
                      {category.name}
                    </Link>
                  )}
                </div>
                <h1 onClick={() => window.__ADMIN_CONTROL_MODE__ && props.syncProduct()} className='title fade-in second'>
                  {product?.title}
                </h1>
                {!!product?.curatorCount && (
                  <div className='curator-count fade-in second'>
                    <CuratorCountDisplay
                      count={product.curatorCount}
                      circleCount={product.circleCuratorCount}
                      onClick={props.scrollToMyCircle}
                      curators={product.curators}
                      circleCurators={product.circleCurators}
                    />
                  </div>
                )}
              </>
            )}
            {product && !isFetchingFull && (
              <div className='favorite-icon fade-in second'>
                <TrainingTooltip
                  numDismissalsToHide={window.__IS_PROD__ ? 3 : 1_000}
                  fadeInDelay={3_000}
                  fadeOutDelay={10_000}
                  visible={showAddProductTrainingTooltip}
                  trainingKey={tooltipKeys.ADD_TO_WISHLIST_FROM_PRODUCT_PAGE}
                  message={
                    <>
                      Add this product to <b>wishlist</b> to save it for later.
                    </>
                  }
                  position='left'
                >
                  <ProductHeart
                    size={24}
                    product={product}
                    user={user}
                    attribution={{
                      Curator_id: getAttributableCuratorId(shop),
                      CuratorGroup_id: getAttributableCuratorGroupId(shop)
                    }}
                  />
                </TrainingTooltip>
              </div>
            )}
          </div>
          {isFetchingFull ? (
            <div className='body'>
              <div className='description emphasize loading' />
              <div className='description normal loading' />
            </div>
          ) : (
            description && (
              <h2 className='body'>
                <div className={cn('description emphasize fade-in third')}>{firstSentence}</div>
                <div className={cn('description normal fade-in fourth')}>{restOfDescription}</div>
              </h2>
            )
          )}
          {isFetchingFull ? (
            <div className='links loading' />
          ) : (
            <div className='links fade-in fifth'>
              {primaryLink ? (
                <div className='link-options'>
                  <a
                    className={cn('primary-link', { 'out-of-stock': isLinkOutOfStock(primaryLink) })}
                    onClick={() => registerPinClickEventForProduct(product, { domain: primaryLink.domain, user })}
                    href={addVariablesToShopAffiliateLink(primaryLink.affiliate_link, { domain: primaryLink.domain, product, shop, user })}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {getLinkPriceDisplay(primaryLink) ? (
                      <>
                        {getLinkPriceDisplay(primaryLink) && (
                          <span className='price'>
                            {isLinkDiscounted(primaryLink) ? (
                              <>
                                <span className='strikethrough'>{getLinkStandardPriceDisplay(primaryLink)}</span>
                                <span className='discounted-price'>
                                  {` `}
                                  {getLinkPriceDisplay(primaryLink)}
                                </span>
                              </>
                            ) : (
                              getLinkPriceDisplay(primaryLink)
                            )}
                          </span>
                        )}
                        {isLinkOutOfStock(primaryLink)
                          ? primaryLink.merchant
                            ? `Out of Stock at ${primaryLink.merchant.name}`
                            : 'Out of Stock'
                          : primaryLink.merchant
                          ? `Shop now at ${primaryLink.merchant.name}`
                          : 'Shop now'}
                      </>
                    ) : primaryLink.merchant ? (
                      `Shop now at ${primaryLink.merchant.name}`
                    ) : (
                      'Shop now'
                    )}
                  </a>
                  <div className='or-divider'>
                    <div className='or-divider-line' />
                    <div className='or-divider-text'>or</div>
                    <div className='or-divider-line' />
                  </div>
                  <div onClick={props.toggleAllLinksModal} className='all-links-toggle'>
                    {links.length > 1
                      ? `See ${links.length - 1 > 25 ? '25+' : links.length - 1} other buying option${links.length > 2 ? 's' : ''}`
                      : 'See all buying Options'}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              ) : (
                product?.backupAffiliateLink && (
                  <div className='link-options'>
                    <a
                      className='primary-link'
                      onClick={() => registerPinClickEventForProduct(product, { domain: getDomainFromUrl(product.fallbackUrl), user })}
                      href={addVariablesToShopAffiliateLink(product.backupAffiliateLink, {
                        domain: getDomainFromUrl(product.fallbackUrl),
                        product,
                        shop,
                        user
                      })}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {product.backupAffiliateLinkPrice && (
                        <span className='price'>
                          {formatAmountInCurrency(product.backupAffiliateLinkPrice, product.backupAffiliateLinkPriceCurrency)}
                        </span>
                      )}
                      {product.merchant ? `Shop now at ${product.merchant.name}` : 'Shop now'}
                    </a>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
      <div className={cn('product-images-container', additionalClasses)}>
        <div className={cn('primary-image-container', additionalClasses)}>
          {featuredProductImage && (
            <img
              onLoad={onPrimaryImageLoad}
              className={cn('primary-image', { loaded: primaryImageLoaded }, featuredProductImage?.style)}
              loading='eager'
              src={featuredProductImage.image}
              alt={product?.title}
            />
          )}
          {window.__ADMIN_CONTROL_MODE__ && product?.id && (
            <div className='admin-controls'>
              <a className='control-btn' href={`${process.env.REACT_APP_ADMIN_URL}/products/${product.id}?returnTo=shop`}>
                Edit
              </a>
            </div>
          )}
        </div>
        <div className='other-images'>
          {isFetchingFull
            ? _.range(4).map(index => (
                <div className='product-main-section-other-image-container loading' key={index}>
                  <div className='other-image loading' />
                </div>
              ))
            : visibleOtherImages.map((productImage, index) => {
                const { image, style, id } = productImage;
                const isFeatured = id === featuredProductImage?.id;
                const select = () => setFeaturedProductImage(productImage);
                return (
                  <div className={cn('product-main-section-other-image-container', { featured: isFeatured })} key={id}>
                    <img
                      onClick={select}
                      loading={index <= 6 ? 'eager' : 'lazy'}
                      className={cn('other-image', style, { featured: isFeatured })}
                      onLoad={e => (e.target.naturalWidth < 250 || e.target.naturalHeight < 250) && imageBroke(image)}
                      onError={() => imageBroke(image)}
                      src={image}
                      alt={product?.title}
                    />
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
};

ProductMainSection.propTypes = {
  // From Outside
  product: PropTypes.object,
  isFetchingFull: PropTypes.bool.isRequired,
  syncProduct: PropTypes.func.isRequired,
  scrollToMyCircle: PropTypes.func.isRequired,
  scrollToCurators: PropTypes.func.isRequired,
  toggleAllLinksModal: PropTypes.func.isRequired,

  // From Inside
  shop: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  deleteUserProductByProductId: PropTypes.func.isRequired,
  createUserProductByProductId: PropTypes.func.isRequired,
  lastLocation: PropTypes.object,
  history: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { shop, user } = state;
  return { shop, user };
};

export default connect(mapStateToProps, {
  deleteUserProductByProductId,
  createUserProductByProductId
})(withLastLocation(withRouter(ProductMainSection)));
